<template>
  <div class="main-layout" :class="{ 'is-mobile': isMobile }">
    <!-- 顶部导航栏 -->
    <el-header class="header">
      <div class="header-content">
        <div class="header-left">
          <!-- 移动端菜单按钮 -->
          <el-button 
            class="mobile-menu-btn" 
            icon="el-icon-menu" 
            @click="drawerVisible = true"
            circle
            size="small"
          ></el-button>
          <div class="logo">
            <i class="el-icon-document"></i>
            <span class="title">文档管理系统</span>
          </div>
        </div>
        <div class="user-info">
          <el-dropdown @command="handleUserCommand">
            <span class="user-dropdown">
              <el-avatar :size="32" icon="el-icon-user-solid"></el-avatar>
              <span class="username">
                {{ userInfo.username || '用户' }}
                <el-tag size="mini" :type="roleTagType" style="margin-left: 8px;" class="role-tag">
                  {{ roleText }}
                </el-tag>
              </span>
              <i class="el-icon-arrow-down el-icon--right"></i>
            </span>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item disabled>
                <i class="el-icon-user"></i> {{ userInfo.username }}
              </el-dropdown-item>
              <el-dropdown-item divided command="logout">
                <i class="el-icon-switch-button"></i> 退出登录
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
      </div>
    </el-header>
    
    <!-- 主要内容区域 -->
    <el-container class="main-container">
      <!-- 侧边栏（桌面端） -->
      <el-aside width="200px" class="sidebar desktop-sidebar">
        <el-menu
          :default-active="activeMenu"
          class="sidebar-menu"
          background-color="#304156"
          text-color="#bfcbd9"
          active-text-color="#409EFF"
          router
        >
          <!-- <el-menu-item index="/admin/dashboard">
            <i class="el-icon-s-home"></i>
            <span slot="title">仪表盘</span>
          </el-menu-item> -->
          <el-menu-item index="/admin/users">
            <i class="el-icon-user-solid"></i>
            <span slot="title">用户管理</span>
          </el-menu-item>
          <el-menu-item index="/admin/login-logs">
            <i class="el-icon-document"></i>
            <span slot="title">登录记录</span>
          </el-menu-item>
          <el-menu-item index="/admin/oss-config">
            <i class="el-icon-upload"></i>
            <span slot="title">OSS配置</span>
          </el-menu-item>
          <el-menu-item index="/admin/documents">
            <i class="el-icon-document"></i>
            <span slot="title">文档管理</span>
          </el-menu-item>
        </el-menu>
      </el-aside>
      
      <!-- 移动端抽屉 -->
      <el-drawer
        title="菜单"
        :visible.sync="drawerVisible"
        direction="ltr"
        :with-header="true"
        size="250px"
        class="mobile-drawer"
      >
        <el-menu
          :default-active="activeMenu"
          class="sidebar-menu"
          background-color="#304156"
          text-color="#bfcbd9"
          active-text-color="#409EFF"
          router
          @select="handleMenuSelect"
        >
          <el-menu-item index="/admin/users">
            <i class="el-icon-user-solid"></i>
            <span slot="title">用户管理</span>
          </el-menu-item>
          <el-menu-item index="/admin/login-logs">
            <i class="el-icon-document"></i>
            <span slot="title">登录记录</span>
          </el-menu-item>
          <el-menu-item index="/admin/oss-config">
            <i class="el-icon-upload"></i>
            <span slot="title">OSS配置</span>
          </el-menu-item>
          <el-menu-item index="/admin/documents">
            <i class="el-icon-document"></i>
            <span slot="title">文档管理</span>
          </el-menu-item>
        </el-menu>
      </el-drawer>
      
      <!-- 主内容区 -->
      <el-main class="main-content">
        <router-view />
      </el-main>
    </el-container>
  </div>
</template>

<script>
import { userUtils, authUtils } from '@/utils/auth'

export default {
  name: 'MainLayout',
  data() {
    return {
      userInfo: {},
      drawerVisible: false
    }
  },
  computed: {
    activeMenu() {
      const path = this.$route.path
      // 处理管理后台路由
      if (path.startsWith('/admin/documents')) {
        return '/admin/documents'
      }
      if (path.startsWith('/admin/users') || path === '/admin' || path === '/admin/') {
        return '/admin/users'
      }
      if (path.startsWith('/admin/login-logs')) {
        return '/admin/login-logs'
      }
      if (path.startsWith('/admin/oss-config')) {
        return '/admin/oss-config'
      }
      return path
    },
    isAdmin() {
      return authUtils.isAdmin()
    },
    isEditor() {
      return authUtils.isEditor()
    },
    roleText() {
      const role = this.userInfo.role
      const roleMap = {
        'admin': '管理员',
        'editor': '编辑员',
        'viewer': '普通用户'
      }
      return roleMap[role] || '未知'
    },
    roleTagType() {
      const role = this.userInfo.role
      const typeMap = {
        'admin': 'danger',
        'editor': 'warning',
        'viewer': 'info'
      }
      return typeMap[role] || 'info'
    },
    isMobile() {
      // 优先使用设备检测，避免横屏时被误判为桌面端
      if (typeof navigator !== 'undefined') {
        const ua = navigator.userAgent || navigator.vendor || ''
        
        // 1. 检测明确的移动设备标识
        const mobileRegex = /(android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini)/i
        if (mobileRegex.test(ua)) {
          return true
        }
        
        // 2. 检测移动设备的User Agent特征
        const mobilePlatforms = /Mobile|mobile|Mobi|mobi/i
        if (mobilePlatforms.test(ua)) {
          return true
        }
        
        // 3. 检测触摸设备并结合屏幕尺寸
        if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
          // 使用屏幕的最小尺寸来判断，避免横屏影响
          const screenMinSize = window.screen ? Math.min(window.screen.width, window.screen.height) : 0
          const screenMaxSize = window.screen ? Math.max(window.screen.width, window.screen.height) : 0
          
          // 如果最小尺寸小于等于768，或者最大尺寸小于等于1024，很可能是移动设备 
          if (screenMinSize <= 768 || (screenMaxSize <= 1024 && screenMinSize <= 768)) {
            return true
          }
        }
      }
      
      // 4. 兜底：如果以上都不满足，但当前窗口宽度小于等于768，也认为是移动端
      return window.innerWidth <= 768
    }
  },
  mounted() {
    if (!authUtils.isAdmin()) {
      this.$message.warning('无权限访问管理后台，已自动返回文档列表')
      this.$router.replace('/')
      return
    }
    this.getUserInfo()
  },
  methods: {
    getUserInfo() {
      this.userInfo = userUtils.getUserInfo() || {}
    },
    
    handleUserCommand(command) {
      switch (command) {
        case 'profile':
          this.$message.info('个人资料功能开发中...')
          break
        case 'settings':
          this.$message.info('系统设置功能开发中...')
          break
        case 'logout':
          this.logout()
          break
      }
    },
    
    logout() {
      this.$confirm('确定要退出登录吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        // 清除所有认证信息
        authUtils.clearAuth()
        
        this.$message.success('已退出登录')
        
        // 跳转到登录页
        this.$router.push('/login')
      }).catch(() => {
        this.$message.info('已取消退出')
      })
    },
    
    handleMenuSelect() {
      // 移动端选择菜单后关闭抽屉
      // 优先使用设备检测，避免横屏时被误判为桌面端
      const isMobile = (() => {
        if (typeof navigator !== 'undefined') {
          const ua = navigator.userAgent || navigator.vendor || ''
          const mobileRegex = /(android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini)/i
          if (mobileRegex.test(ua)) {
            return true
          }
          // 检测触摸设备
          if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
            // 进一步检测是否为移动设备（排除触摸屏电脑）
            const isMobileDevice = /Mobi|Android/i.test(ua) || 
                                   (window.screen && window.screen.width <= 1024 && window.screen.height <= 1024)
            if (isMobileDevice) {
              return true
            }
          }
        }
        // 兜底使用屏幕宽度检测
        return window.innerWidth <= 768
      })()
      
      if (isMobile) {
        this.drawerVisible = false
      }
    }
  }
}
</script>

<style scoped>
.main-layout {
  height: 100vh;
  display: flex;
  flex-direction: column;
}

.header {
  background: #fff;
  border-bottom: 1px solid #e6e6e6;
  padding: 0;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;
  padding: 0 20px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.mobile-menu-btn {
  display: none;
}

.logo {
  display: flex;
  align-items: center;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.logo i {
  margin-right: 8px;
  font-size: 20px;
  color: #409EFF;
}

.user-dropdown {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 4px;
  transition: background-color 0.3s;
}

.user-dropdown:hover {
  background-color: #f5f7fa;
}

.username {
  margin: 0 8px;
  color: #333;
}

.role-tag {
  display: inline-block;
}

.main-container {
  flex: 1;
  height: calc(100vh - 60px);
}

.sidebar {
  background-color: #304156;
}

.desktop-sidebar {
  display: block;
}

.sidebar-menu {
  height: 100%;
  border-right: none;
}

.main-content {
  background-color: #f0f2f5;
  padding: 20px;
}

.mobile-drawer ::v-deep .el-drawer__body {
  padding: 0;
}

.mobile-drawer ::v-deep .el-drawer__header {
  margin-bottom: 0;
  padding: 20px;
  background-color: #304156;
  color: #bfcbd9;
}

.mobile-drawer ::v-deep .el-drawer__title {
  color: #bfcbd9;
}

/* 响应式设计 */
.main-layout.is-mobile .desktop-sidebar {
  display: none !important;
}

.main-layout.is-mobile .mobile-menu-btn {
  display: block !important;
}

.main-layout.is-mobile .main-content {
  padding: 10px;
}

.main-layout.is-mobile .logo .title {
  font-size: 16px;
}

.main-layout.is-mobile .role-tag {
  display: none;
}

.main-layout.is-mobile .user-dropdown {
  padding: 4px 8px;
}

/* 小屏幕额外优化 */
@media (max-width: 480px) {
  .header-content {
    padding: 0 10px;
  }
  
  .logo .title {
    font-size: 14px;
  }
  
  .main-content {
    padding: 8px;
  }
}
</style>

